/**
 * Helper function to extract the ID parameter from the URL query parameters
 *
 * The function returns the sanitized value of the 'id' parameter if it exists, otherwise an empty string.
 * If the 'id' parameter is missing or contains invalid characters, an error should be logged.
 *
 * Only alphanumeric characters, hyphens, and underscores are allowed (integers and UUIDs)
 * This prevents path/URL injection and XSS when the value is later interpolated into API URLs or the DOM.
 *
 * @param queryParams - The URL query parameters
 * @param customParam - Optional custom ID parameter name to extract from the query parameters instead of ID
 */

const SAFE_ID_PATTERN = /^[a-zA-Z0-9_.-]+$/

export const getIdFromUrl = (
  queryParams: Record<string, string | undefined>,
  customParam?: string,
): string => {
  const paramName = customParam ?? 'id'
  const id = queryParams[paramName]

  if (!id) {
    // TODO: (SENTRY) Send to sentry when URL doesn't have an expected ID parameter
    console.error(`ID parameter '${paramName}' is missing in the URL`)
    return ''
  }

  const trimmedId = id.trim()

  if (!SAFE_ID_PATTERN.test(trimmedId)) {
    // TODO: (SENTRY) Send to sentry when URL ID parameter contains invalid characters
    console.error(`ID parameter '${paramName}' contains invalid characters`)
    return ''
  }

  return trimmedId
}
